import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  LinearProgress,
  Link,
  TextField,
  Typography,
} from '@mui/material'
import type { ModelMessage } from 'ai'
import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'
import { generatePromptSpec } from '../generate-prompt.js'
import type { AIContext, AIContextSettings } from '../schema/ai-schema.js'
import type { AIProvider } from '../schema/providers.js'

/** Extract value from an input change event without depending on DOM types */
const getInputValue = (e: unknown) => (e as { target: { value: string } }).target.value

const defaultSamplePrompts: Record<AIContext, string> = {
  global: 'Write a short creative greeting for a musician',
  'song:section': 'Write a verse about walking in the rain',
  'song:line': 'Continue this line: The morning light...',
  'song:image': 'Create an image for a song about the ocean at sunset',
  'song:music': 'Describe music for an upbeat summer song',
  advisor: 'What chord progression works well for a ballad?',
}

type PreviewMode = 'text' | 'image' | 'music'

const contextPreviewMode: Record<AIContext, PreviewMode> = {
  global: 'text',
  'song:section': 'text',
  'song:line': 'text',
  'song:image': 'image',
  'song:music': 'music',
  advisor: 'text',
}

export interface AIConfigPreviewProps {
  context: AIContext
  settings: AIContextSettings
  /** Streaming text result (used for text preview and image/music description step) */
  textResult?: { text: string; pending: boolean } | null
  /** URL of a generated preview image (for song:image context) */
  imageUrl?: string | null
  /** Whether image generation is in progress */
  imagePending?: boolean
  /** URL of a generated preview music clip (for song:music context) */
  musicUrl?: string | null
  /** Whether music generation is in progress */
  musicPending?: boolean
  /** Generate text (streaming completion) */
  onGenerateText: (params: { provider: AIProvider; model: string; prompt: ModelMessage[]; requestId: string }) => void
  /** Generate an image from a description prompt (for song:image context) */
  onGenerateImage?: (params: { description: string; model: string }) => void
  /** Generate a music clip from a description prompt (for song:music context) */
  onGenerateMusic?: (params: { description: string; model: string }) => void
  /** Called when context changes so consumer can clear stale preview state */
  onContextChange?: () => void
}

export const AIConfigPreview: FC<AIConfigPreviewProps> = props => {
  const {
    context,
    settings,
    textResult,
    imageUrl,
    imagePending,
    musicUrl,
    musicPending,
    onGenerateText,
    onGenerateImage,
    onGenerateMusic,
    onContextChange,
  } = props

  const mode = contextPreviewMode[context]
  const [samplePrompt, setSamplePrompt] = useState(defaultSamplePrompts[context])
  const [showPromptPreview, setShowPromptPreview] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Local copy of the text result, reset on each new generation to avoid stale data
  const [localResult, setLocalResult] = useState<{ text: string; pending: boolean } | null>(null)
  const [localPending, setLocalPending] = useState(false)

  // Reset sample prompt and notify consumer when context changes
  useEffect(() => {
    setSamplePrompt(defaultSamplePrompts[context])
    setError(null)
    setLocalResult(null)
    setLocalPending(false)
    onContextChange?.()
  }, [context, onContextChange])

  // Sync incoming textResult to local state (only forward, never stale)
  useEffect(() => {
    if (textResult != null) {
      setLocalResult(textResult)
      if (!textResult.pending) {
        setLocalPending(false)
      }
    }
  }, [textResult])

  // Track the resolved model from the last text generation for use in image generation
  const [resolvedModel, setResolvedModel] = useState<string | null>(null)

  // Ref to prevent double-triggering auto image/music generation
  const autoTriggeredRef = useRef(false)

  const handleGenerate = () => {
    setError(null)
    setLocalResult(null)
    setLocalPending(true)
    autoTriggeredRef.current = false
    try {
      const spec = generatePromptSpec({
        context,
        userConfig: settings,
        promptText: samplePrompt,
      })
      setResolvedModel(spec.model)
      onGenerateText({
        provider: spec.provider,
        model: spec.model,
        prompt: spec.prompt,
        requestId: `preview-${Date.now()}`,
      })
    } catch (e) {
      setLocalPending(false)
      setError(e instanceof Error ? e.message : 'Failed to generate prompt')
    }
  }

  // Build the prompt preview messages (best-effort, don't throw)
  let previewMessages: ModelMessage[] = []
  try {
    const spec = generatePromptSpec({
      context,
      userConfig: settings,
      promptText: samplePrompt || 'test',
    })
    previewMessages = spec.prompt
  } catch {
    // Ignore — preview is informational
  }

  const isTextPending = localPending || (localResult?.pending ?? false)
  const textDone = localResult != null && !localResult.pending && localResult.text.length > 0

  // Auto-trigger image/music generation when the text description completes
  useEffect(() => {
    if (autoTriggeredRef.current || !textDone || !resolvedModel || !localResult?.text) {
      return
    }
    if (mode === 'image' && onGenerateImage) {
      autoTriggeredRef.current = true
      onGenerateImage({ description: localResult.text, model: resolvedModel })
    } else if (mode === 'music' && onGenerateMusic) {
      autoTriggeredRef.current = true
      onGenerateMusic({ description: localResult.text, model: resolvedModel })
    }
  }, [textDone, resolvedModel, localResult?.text, mode, onGenerateImage, onGenerateMusic])

  const generateLabel =
    mode === 'image' ? 'Generate Image' : mode === 'music' ? 'Generate Music Clip' : 'Generate Preview'

  const isAssetMode = mode === 'image' || mode === 'music'
  const [showDescription, setShowDescription] = useState(false)

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" gutterBottom>
        Preview
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        {mode === 'image'
          ? 'Test your image style settings by generating a preview image. This uses AI credits.'
          : mode === 'music'
            ? 'Test your music style settings by generating a preview clip. This uses AI credits.'
            : 'Test your settings by generating a sample response. This uses AI credits.'}
      </Typography>

      <TextField
        fullWidth
        size="small"
        label="Sample prompt"
        value={samplePrompt}
        onChange={e => setSamplePrompt(getInputValue(e))}
        sx={{ mb: 1.5 }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="small"
          onClick={handleGenerate}
          disabled={isTextPending || !samplePrompt || (isAssetMode && (imagePending || musicPending))}
          startIcon={
            isTextPending || (isAssetMode && (imagePending || musicPending)) ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {isTextPending
            ? isAssetMode
              ? 'Preparing...'
              : 'Generating...'
            : imagePending
              ? 'Generating image...'
              : musicPending
                ? 'Generating clip...'
                : generateLabel}
        </Button>

        <Link
          component="button"
          variant="caption"
          onClick={() => setShowPromptPreview(p => !p)}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
        >
          {showPromptPreview ? 'Hide prompt' : 'Show prompt'}
        </Link>
      </Box>

      <Collapse in={showPromptPreview}>
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            bgcolor: 'action.hover',
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            maxHeight: 300,
            overflow: 'auto',
          }}
        >
          {previewMessages.map((msg, i) => (
            <Box key={i} sx={{ mb: 1 }}>
              <Typography
                component="span"
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  color: msg.role === 'system' ? 'info.main' : 'success.main',
                  fontFamily: 'monospace',
                }}
              >
                [{msg.role}]
              </Typography>{' '}
              <Typography
                component="span"
                sx={{ fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
              >
                {typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>

      {error && (
        <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
          {error}
        </Typography>
      )}

      {isTextPending && <LinearProgress sx={{ mb: 1 }} />}

      {/* Text result — shown inline for text mode, collapsible for image/music */}
      {localResult?.text && !isAssetMode && (
        <Box
          sx={{
            p: 2,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            whiteSpace: 'pre-wrap',
            fontSize: '0.875rem',
            lineHeight: 1.6,
            mb: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Result:
          </Typography>
          {localResult.text}
        </Box>
      )}

      {/* Rendered image */}
      {imageUrl && (
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            maxWidth: 512,
            mb: 2,
          }}
        >
          <img src={imageUrl} alt="AI generated preview" style={{ width: '100%', display: 'block' }} />
        </Box>
      )}

      {/* Audio player for generated music */}
      {musicUrl && (
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            maxWidth: 512,
            mb: 2,
          }}
        >
          <audio controls src={musicUrl} style={{ width: '100%' }} />
        </Box>
      )}

      {/* Collapsible description for image/music modes */}
      {isAssetMode && localResult?.text && (
        <>
          <Link
            component="button"
            variant="caption"
            onClick={() => setShowDescription(p => !p)}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 1 }}
          >
            {showDescription ? 'Hide description' : 'Show description'}
          </Link>
          <Collapse in={showDescription}>
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
                whiteSpace: 'pre-wrap',
                fontSize: '0.8rem',
                mb: 2,
              }}
            >
              {localResult.text}
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  )
}

export default AIConfigPreview
